import { canApplySnapshot } from '../../services/realtime-snapshot';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../store';
import { AuthUtils } from '../../utils/helper_functions';
import { fetchUser } from '../auth-store';

/** Dynamic powerup from Kokki system */
interface RuntimePowerupFE {
  templateId: number;
  effectKey: string;
  name: string;
  description: string | null;
  svgIcon: string | null;
  effectGif: string | null;
  type: string; // 'normal' | 'ultimate'
  charges: number;
  maxCharges: number;
}

interface RuntimeKokkiEmoteFE {
  id: number;
  name: string;
  imageUrl: string;
}

interface Player {
  id: string;
  name: string;
  points: number;
  avatarId: number;
  kokkiId?: number;
  kokkiImageUrl?: string | null;
  isGuest: boolean;
  isBot?: boolean;
  botDifficulty?: string;
  botProfileId?: number | null;
  isConnected?: boolean;
  powerUps: RuntimePowerupFE[];
  emotes?: RuntimeKokkiEmoteFE[];
  // Pending powerup effects
  noChoicesActive?: boolean;
  diceMultiplier?: number;
  bonusTime?: number;
}

interface AuthoritativeTimer {
  kind: string;
  phase: string;
  startedAt: number;
  endsAt: number;
  durationMs: number;
  remainingMs: number;
}

interface GameState {
  gameId: number | null;
  adminId: number | null;
  adminName: string | null;
  categories: CategoryState[]; // Define proper types if available
  players: Player[];
  currentPlayerIndex: number;
  status: 'loaded' | 'ready' | 'avatar_selection' | 'ongoing' | 'completed' | null;
  phase: 'waiting' | 'avatar_selection' | 'picking' | 'answering' | null;
  phaseMessage: string;
  error: string | null;
  isAdmin: boolean;
  currentFlippedCardId: number | null;
  timerStartTime: number | null;  // timestamp when the current phase started
  timerDuration: number | null;   // duration in milliseconds for the current phase
  timerEndTime: number | null;  // Add this new property
  stealerId: string | null;
  nextPlayerId: string | null;
  doublePointCardId: number | null;
  // Hop mode specific fields
  isHopMode?: boolean;
  hopSessionId?: number;
  avatarSelectionDeadline?: number;
  revision?: number;
  generation?: string;
  privateRevision?: number;
  serverNow?: number;
  timer?: AuthoritativeTimer | null;
  viewerPlayerId?: string;
  playerState?: Player;
}

 interface CategoryState {
  id: number;
  name: string;
  cards: CardState[];
}

 export interface CardState {
  id: number;
  question: string;
  numberOfPoints: number;
  flipped: boolean;
  answered: boolean;
  playerName?: string;
  userAnswer?: string;
  isCorrect?: boolean;
  answers?: (string | null)[];
  correctAnswer: string;

  showAnswers: boolean;
  hasRisk?: boolean;
}

const initialState: GameState = {
  gameId: null,
  adminId: null,
  adminName: null,
  categories: [],
  players: [],
  currentPlayerIndex: 0,
  status: null,
  phase: null,
  phaseMessage: '',
  error: null,
  isAdmin: false,
  currentFlippedCardId: null,
  timerStartTime: null,
  timerDuration: null,
  timerEndTime: null,
  stealerId: null,
  nextPlayerId: null,
  doublePointCardId: null,
  // Hop mode specific fields
  isHopMode: false,
  hopSessionId: undefined,
  avatarSelectionDeadline: undefined,
  revision: undefined,
  serverNow: undefined,
  timer: null,
  viewerPlayerId: undefined,
  playerState: undefined,
};

const normalizeIncomingGameState = (gameState: GameState): GameState => {
  const normalizedState: GameState = { ...gameState };

  if (gameState.timer) {
    normalizedState.timerStartTime = gameState.timer.startedAt;
    normalizedState.timerDuration = gameState.timer.durationMs;
    normalizedState.timerEndTime = gameState.timer.endsAt;
  } else if (gameState.timer === null) {
    normalizedState.timerStartTime = null;
    normalizedState.timerDuration = null;
    normalizedState.timerEndTime = null;
  } else if (
    normalizedState.timerStartTime !== null &&
    normalizedState.timerStartTime !== undefined &&
    normalizedState.timerDuration !== null &&
    normalizedState.timerDuration !== undefined
  ) {
    normalizedState.timerEndTime = normalizedState.timerEndTime ?? (normalizedState.timerStartTime + normalizedState.timerDuration);
  }

  return normalizedState;
};

const mergeAuthoritativeGameState = (state: GameState, incomingGameState: GameState): GameState => {
  const normalizedIncomingState = normalizeIncomingGameState(incomingGameState);

  if (!canApplySnapshot(state, normalizedIncomingState)) return state;
  if (normalizedIncomingState.generation && normalizedIncomingState.generation !== state.generation) state = initialState;

  const mergedState: GameState = {
    ...state,
    ...normalizedIncomingState,
  };

  if (normalizedIncomingState.viewerPlayerId === undefined && state.viewerPlayerId !== undefined) {
    mergedState.viewerPlayerId = state.viewerPlayerId;
  }

  if (normalizedIncomingState.playerState === undefined && state.playerState !== undefined) {
    mergedState.playerState = state.playerState;
  }

  return mergedState;
};

// Thunk to set game state with isAdmin determined from the user state
export const setGameStateThunk = createAsyncThunk<
  GameState,
  GameState,
  { state: RootState }
>('game/setGameStateThunk', async (gameState, { getState, dispatch }) => {
  const normalizedGameState = normalizeIncomingGameState(gameState);
  const state = getState();
  const userId = state.user.userInfo?.id;
  const appDispatch = dispatch as AppDispatch;
  let isAdmin = false;

  if (userId !== undefined && userId === normalizedGameState.adminId) {
    isAdmin = true;
  } else if (AuthUtils.getAuthToken()) {
    // Try to fetch user data if token exists but userInfo is null
    try {
      await appDispatch(fetchUser()).unwrap();
      const updatedState = getState(); // Get the updated state
      const newUserId = updatedState.user.userInfo?.id;
      isAdmin = newUserId !== undefined && newUserId === normalizedGameState.adminId;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }
  return { ...normalizedGameState, isAdmin };
});

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    // Remove the setGameState reducer if you replace it with the thunk
    resetGameState() {
      return initialState;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    setGameId(state, action: PayloadAction<string>) {
      state.gameId = parseInt(action.payload);
      localStorage.setItem('currentGameId', action.payload);
    },
    clearGameState(state) {
      localStorage.removeItem('currentGameId');
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setGameStateThunk.fulfilled, (state, action) => {
        return mergeAuthoritativeGameState(state, action.payload);
      })
      .addCase(setGameStateThunk.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to set game state';
      });
    // ... handle other thunks like fetchUser ...
  },
});

export const { resetGameState, setError } = gameSlice.actions;
export default gameSlice.reducer;
